import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getMe, logout as logoutApi } from '../api/auth';
import { setTenantId, UnauthorizedError } from '../api/client';
import { clearPagesCache } from '../hooks/usePages';
import { clearTenantsCache } from '../hooks/useTenants';
import { clearRolesCache } from '../hooks/useRoles';
import { clearIsaAssetTypesCache } from '../hooks/useIsaAssetTypes';
import { clearAssetTypesCache } from '../hooks/useAssetTypes';
import { clearBaseAssetTypesCache } from '../hooks/useBaseAssetTypes';
import { clearCreditRiskTypesCache } from '../hooks/useCreditRiskTypes';
import { clearCrciesCache } from '../hooks/useCrcies';
import { clearLendingFeeCalcTypesCache } from '../hooks/useLendingFeeCalcTypes';
import { clearPriceTypesCache } from '../hooks/usePriceTypes';
import { clearBenchmarkRatesCache } from '../hooks/useBenchmarkRates';
import { clearBenchmarkRatePeriodsCache } from '../hooks/useBenchmarkRatePeriods';
import { clearSourcesCache } from '../hooks/useSources';
import { clearInventoryRowTypesCache } from '../hooks/useInventoryRowTypes';
import { clearBalanceTypesCache } from '../hooks/useBalanceTypes';
import { clearCreditSourceGroupsCache } from '../hooks/useCreditSourceGroups';
import { clearCategoriesCache } from '../hooks/useCategories';
import { clearCalcMethodsCache } from '../hooks/useCalcMethods';
import { clearIdxTypesCache } from '../hooks/useIdxTypes';
import { clearGroupingLevelsCache } from '../hooks/useGroupingLevels';
import { clearBfixRateTypesCache } from '../hooks/useBfixRateTypes';
import { clearIndicesCache } from '../hooks/useIndices';
import { clearAlertsCache } from '../hooks/useAlerts';
import { clearValuationParamsCache } from '../hooks/useValuationParams';
import {
  clearFundClassificationsCache,
  clearIsaFundClassificationsCache,
  clearProspectusExposureTypesCache,
  clearOperatorsCache,
  clearTrusteesCache,
  clearCompaniesCache,
} from '../hooks/fundLookups';
import { clearRecentPages } from '../hooks/useRecentPages';
import { clearToasts, setToastUserContext } from '../components/ErrorToast';
import { clearTabs } from '../tabs';
import { AuthenticatedUser } from '../types/auth';

type AuthStatus = 'bootstrapping' | 'unauthenticated' | 'authenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  login: (user: AuthenticatedUser) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('bootstrapping');
  const [user, setUser] = useState<AuthenticatedUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        setUser(res.user);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (!(err instanceof UnauthorizedError)) {
          console.error('AuthProvider: bootstrap failed', err);
        }
        setUser(null);
        setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setTenantId(user?.currentTenant.tenantId ?? null);
  }, [user]);

  const login = useCallback((nextUser: AuthenticatedUser) => {
    setUser(nextUser);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch (err) {
      console.error('AuthProvider: logout API call failed; clearing local state anyway', err);
    }
    // Drop in-memory caches keyed by the current session so the next login
    // re-fetches fresh data (including permission-filtered /api/pages).
    clearPagesCache();
    clearTenantsCache();
    clearRolesCache();
    clearIsaAssetTypesCache();
    clearAssetTypesCache();
    clearBaseAssetTypesCache();
    clearCreditRiskTypesCache();
    clearCrciesCache();
    clearLendingFeeCalcTypesCache();
    clearPriceTypesCache();
    clearBenchmarkRatesCache();
    clearBenchmarkRatePeriodsCache();
    clearSourcesCache();
    clearInventoryRowTypesCache();
    clearBalanceTypesCache();
    clearCreditSourceGroupsCache();
    clearCategoriesCache();
    clearCalcMethodsCache();
    clearIdxTypesCache();
    clearGroupingLevelsCache();
    clearBfixRateTypesCache();
    clearIndicesCache();
    clearAlertsCache();
    clearValuationParamsCache();
    clearFundClassificationsCache();
    clearIsaFundClassificationsCache();
    clearProspectusExposureTypesCache();
    clearOperatorsCache();
    clearTrusteesCache();
    clearCompaniesCache();
    // Wipe persisted recently-visited screens so the next user can't see them.
    clearRecentPages();
    // Wipe persisted internal tabs (banking privacy — the next user on a
    // shared browser must not see the prior user's open tabs). Also resets
    // any still-mounted TabsProvider immediately (closeAllTabs()).
    clearTabs();
    // An open error toast carries the user's email + tenant in its details (banking privacy on a shared browser).
    clearToasts();
    setToastUserContext(null, null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value: AuthContextValue = { status, user, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
